import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';
import api from '../api/client';

/**
 * SecureRecordingRedirect
 *
 * Auth-gated interview recording player.
 *
 * 1. User clicks the recording link (report page or downloaded PDF)
 * 2. Browser opens /secure-recording/:sessionId
 * 3. If NOT logged in -> redirect to /login with redirectTo
 * 4. If logged in -> call backend secure API -> play the recording in-page,
 *    with a sidebar to switch between the full interview and each question.
 */

const FULL_KEY = 'full';
/** Same sky -> fuchsia gradient as the dashboard navbar / sidebar. */
const BRAND_GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';
const selectedItemClass = 'text-white shadow-md shadow-fuchsia-500/20';
const idleItemClass = 'text-gray-700 hover:bg-sky-50';

function formatTimestamp(seconds) {
    if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return null;
    const total = Math.floor(seconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes);
    return `${hours > 0 ? `${hours}:` : ''}${mm}:${String(secs).padStart(2, '0')}`;
}

/** Window this question occupies in the full interview, e.g. "0:22 – 0:30". */
function formatRange(question) {
    const start = formatTimestamp(question?.clip_start_seconds);
    const end = formatTimestamp(question?.clip_end_seconds);
    if (!start) return null;
    return end ? `${start} – ${end}` : start;
}

/** Same 0-10 bands the interview report uses, so colours read consistently. */
function scoreBadgeClass(score) {
    if (score >= 8) return 'bg-emerald-100 text-emerald-800 border-emerald-300';
    if (score >= 6) return 'bg-sky-100 text-sky-800 border-sky-300';
    if (score >= 4) return 'bg-orange-100 text-orange-800 border-orange-300';
    return 'bg-red-100 text-red-800 border-red-300';
}

function formatScore(score) {
    if (typeof score !== 'number' || !Number.isFinite(score)) return null;
    return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

export default function SecureRecordingRedirect() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
    const [error, setError] = useState(null);
    const [videoUrl, setVideoUrl] = useState(null);
    const [sessionMeta, setSessionMeta] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [activeKey, setActiveKey] = useState(FULL_KEY);
    const [showPlayOverlay, setShowPlayOverlay] = useState(true);
    // Which question the full recording is currently playing through. Held in a ref
    // as well so the timeupdate handler only triggers a render when it changes.
    const [liveIndex, setLiveIndex] = useState(null);
    const liveIndexRef = useRef(null);

    // The player owns the whole viewport, so suppress document scrolling while mounted.
    useEffect(() => {
        const html = document.documentElement;
        const { body } = document;
        const prevHtmlOverflow = html.style.overflow;
        const prevBodyOverflow = body.style.overflow;
        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        return () => {
            html.style.overflow = prevHtmlOverflow;
            body.style.overflow = prevBodyOverflow;
        };
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');

        if (!token) {
            navigate('/login', {
                state: { redirectTo: `/secure-recording/${sessionId}` },
                replace: true,
            });
            return undefined;
        }

        let cancelled = false;

        const fetchRecording = async () => {
            try {
                setStatus('loading');
                const response = await api.get(`secure-recording/${sessionId}/`);
                if (cancelled) return;

                const url = response.data?.video_url;
                if (!url) {
                    setStatus('error');
                    setError('No recording URL returned from server.');
                    return;
                }

                setVideoUrl(url);
                setSessionMeta(response.data?.session || null);
                setQuestions(Array.isArray(response.data?.questions) ? response.data.questions : []);
                setActiveKey(FULL_KEY);
                setStatus('ready');
            } catch (err) {
                if (cancelled) return;
                if (err.response?.status === 401) {
                    navigate('/login', {
                        state: { redirectTo: `/secure-recording/${sessionId}` },
                        replace: true,
                    });
                } else if (err.response?.status === 403) {
                    setStatus('error');
                    setError("You don't have permission to access this recording.");
                } else if (err.response?.status === 404) {
                    setStatus('error');
                    setError('Recording not found for this interview session.');
                } else {
                    setStatus('error');
                    setError(err.message || 'Failed to load recording. Please try again.');
                }
            }
        };

        fetchRecording();
        return () => {
            cancelled = true;
        };
    }, [sessionId, navigate]);

    const activeQuestion = useMemo(
        () => questions.find((q) => String(q.index) === String(activeKey)) || null,
        [questions, activeKey]
    );

    // A question either has its own clip file, or is a time window inside the full recording.
    const playback = useMemo(() => {
        if (activeKey === FULL_KEY || !activeQuestion) {
            return { src: videoUrl, seekStart: null, seekEnd: null, title: 'Full Interview' };
        }
        const title = activeQuestion.label || `Question ${activeQuestion.index}`;
        if (activeQuestion.video_url) {
            return { src: activeQuestion.video_url, seekStart: null, seekEnd: null, title };
        }
        return {
            title,
            src: videoUrl,
            seekStart: typeof activeQuestion.clip_start_seconds === 'number' ? activeQuestion.clip_start_seconds : null,
            seekEnd: typeof activeQuestion.clip_end_seconds === 'number' ? activeQuestion.clip_end_seconds : null,
        };
    }, [activeKey, activeQuestion, videoUrl]);

    useEffect(() => {
        const name = sessionMeta?.candidate_name;
        document.title = name ? `Aptiview · ${name}` : `Aptiview · Interview recording #${sessionId}`;
        return () => {
            document.title = 'Aptiview';
        };
    }, [sessionMeta, sessionId]);

    useEffect(() => {
        setShowPlayOverlay(true);
        const el = videoRef.current;
        if (!el) return undefined;

        const startAt = playback.seekStart;
        const applyStart = () => {
            if (startAt == null) return;
            try {
                if (Math.abs(el.currentTime - startAt) > 0.05) {
                    el.currentTime = startAt;
                }
            } catch {
                /* seeking before the browser is ready is not fatal */
            }
        };
        if (el.readyState >= 1) applyStart();

        const onPlay = () => setShowPlayOverlay(false);
        const onPause = () => {
            if (el.ended) setShowPlayOverlay(true);
        };
        el.addEventListener('loadedmetadata', applyStart);
        el.addEventListener('loadeddata', applyStart);
        el.addEventListener('play', onPlay);
        el.addEventListener('pause', onPause);
        return () => {
            el.removeEventListener('loadedmetadata', applyStart);
            el.removeEventListener('loadeddata', applyStart);
            el.removeEventListener('play', onPlay);
            el.removeEventListener('pause', onPause);
        };
    }, [playback.src, playback.seekStart]);

    // The full recording is the per-question clips back to back, so each question's
    // window in it is known exactly and playback position maps straight onto it.
    const trackedQuestions = useMemo(
        () => questions.filter((q) => typeof q.clip_start_seconds === 'number'),
        [questions]
    );

    useEffect(() => {
        const el = videoRef.current;
        const trackable = activeKey === FULL_KEY && trackedQuestions.length > 0;
        if (!el || !trackable) {
            liveIndexRef.current = null;
            setLiveIndex(null);
            return undefined;
        }

        const resolve = () => {
            const { currentTime } = el;
            let current = null;
            for (let i = 0; i < trackedQuestions.length; i += 1) {
                if (currentTime >= trackedQuestions[i].clip_start_seconds) current = trackedQuestions[i].index;
                else break;
            }
            if (current !== liveIndexRef.current) {
                liveIndexRef.current = current;
                setLiveIndex(current);
            }
        };

        resolve();
        el.addEventListener('loadedmetadata', resolve);
        el.addEventListener('timeupdate', resolve);
        el.addEventListener('seeked', resolve);
        return () => {
            el.removeEventListener('loadedmetadata', resolve);
            el.removeEventListener('timeupdate', resolve);
            el.removeEventListener('seeked', resolve);
        };
    }, [activeKey, trackedQuestions, playback.src]);

    // Stop at the end of a question window when it is a slice of the full recording.
    useEffect(() => {
        const el = videoRef.current;
        if (!el || playback.seekEnd == null) return undefined;
        const onTime = () => {
            if (el.currentTime >= playback.seekEnd) el.pause();
        };
        el.addEventListener('timeupdate', onTime);
        return () => el.removeEventListener('timeupdate', onTime);
    }, [playback.src, playback.seekEnd]);

    const isFullRecording = activeKey === FULL_KEY;

    const liveQuestion = useMemo(() => {
        if (!isFullRecording || liveIndex == null) return null;
        return trackedQuestions.find((item) => item.index === liveIndex) || null;
    }, [isFullRecording, liveIndex, trackedQuestions]);

    // Reserved for the whole of full playback, so the video never resizes mid-play.
    const showQuestionPanel = isFullRecording
        ? trackedQuestions.length > 0
        : Boolean(activeQuestion?.question_text);
    const panelQuestion = isFullRecording ? liveQuestion : activeQuestion;

    const fullDurationLabel = trackedQuestions.length
        ? formatTimestamp(trackedQuestions[trackedQuestions.length - 1].clip_end_seconds)
        : null;

    const handleClose = () => {
        try {
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.removeAttribute('src');
                videoRef.current.load();
            }
        } catch {
            /* tearing down the player is best-effort */
        }

        // Opened via target="_blank" in most flows, so closing the tab is the natural
        // action. Browsers block close() for directly-navigated tabs (e.g. a PDF link),
        // so fall back to sending the user back into the app.
        window.close();
        window.setTimeout(() => {
            if (!window.closed) navigate('/', { replace: true });
        }, 150);
    };

    const selectItem = (key) => {
        setActiveKey(key);
        setShowPlayOverlay(false);
        window.requestAnimationFrame(() => {
            const el = videoRef.current;
            if (!el) return;
            el.play().catch(() => setShowPlayOverlay(true));
        });
    };

    const handlePlayClick = () => {
        const el = videoRef.current;
        if (!el) return;
        setShowPlayOverlay(false);
        el.play().catch(() => setShowPlayOverlay(true));
    };

    const header = (
        <header
            className="shrink-0 z-20 text-white shadow-lg shadow-fuchsia-500/15"
            style={{ background: BRAND_GRADIENT }}
        >
            <div className="px-4 py-3.5 flex items-center justify-between gap-4">
                <div className="min-w-0 flex items-center gap-3">
                    <img src="/Aptiview-white.svg" alt="Aptiview" className="h-8 w-auto shrink-0 drop-shadow-sm" />
                    {(sessionMeta?.company_logo || sessionMeta?.company_name) && (
                        <>
                            <span className="text-white/70 text-lg font-light leading-none" aria-hidden>|</span>
                            {sessionMeta.company_logo ? (
                                <img
                                    src={sessionMeta.company_logo}
                                    alt={sessionMeta.company_name || 'Company'}
                                    className="h-7 w-auto max-w-[120px] object-contain bg-white/95 rounded px-1.5 py-0.5"
                                />
                            ) : (
                                <span className="text-sm font-semibold text-white truncate">{sessionMeta.company_name}</span>
                            )}
                        </>
                    )}
                    <div className="min-w-0 border-l border-white/40 pl-4">
                        <p className="text-lg font-bold text-white leading-tight truncate drop-shadow-sm">
                            {sessionMeta?.candidate_name || 'Interview recording'}
                        </p>
                        <p className="text-sm font-medium text-white leading-snug truncate mt-0.5">
                            {sessionMeta?.job_role ? `${sessionMeta.job_role} · ` : ''}
                            Session #{sessionMeta?.id || sessionId}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleClose}
                    className="font-poppins shrink-0 px-3 py-1.5 text-sm font-medium rounded-md border border-white/40 text-white bg-white/15 hover:bg-white/25 backdrop-blur-sm"
                >
                    Close
                </button>
            </div>
        </header>
    );

    const isQuestionDisabled = (q) => !q.video_url && q.clip_start_seconds == null;

    const questionNav = questions.length > 0 ? (
        <>
            <p className="shrink-0 px-5 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Questions
            </p>
            <nav
                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain flex flex-col gap-1 px-3 pb-3"
                aria-label="Interview questions"
            >
                <button
                    type="button"
                    onClick={() => selectItem(FULL_KEY)}
                    className={`font-poppins w-full text-left rounded-lg px-3 py-2.5 text-sm transition-colors ${activeKey === FULL_KEY ? selectedItemClass : idleItemClass}`}
                    style={activeKey === FULL_KEY ? { background: BRAND_GRADIENT } : undefined}
                >
                    <span className="flex items-center gap-2">
                        <span className="font-semibold">Full Interview</span>
                        {fullDurationLabel ? (
                            <span className={`ml-auto shrink-0 text-[10px] tabular-nums ${activeKey === FULL_KEY ? 'text-white/80' : 'text-gray-400'}`}>
                                {fullDurationLabel}
                            </span>
                        ) : null}
                    </span>
                    <span className={`block text-xs mt-0.5 ${activeKey === FULL_KEY ? 'text-white/85' : 'text-gray-500'}`}>
                        Complete session recording
                    </span>
                </button>
                {questions.map((q) => {
                    const key = String(q.index);
                    const selected = activeKey === key;
                    // Softer marker than a real selection: the full recording is still
                    // what is playing, this just tracks where it has got to.
                    const playingNow = isFullRecording && liveIndex === q.index;
                    return (
                        <button
                            key={key}
                            type="button"
                            disabled={isQuestionDisabled(q)}
                            onClick={() => selectItem(key)}
                            className={`font-poppins w-full text-left rounded-lg px-3 py-2.5 text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${selected ? selectedItemClass : playingNow ? 'bg-sky-50 ring-1 ring-sky-300 text-gray-900' : idleItemClass}`}
                            style={selected ? { background: BRAND_GRADIENT } : undefined}
                        >
                            <span className="flex items-center gap-2">
                                <span className="font-semibold">{q.label || `Question ${q.index}`}</span>
                                {formatScore(q.score) ? (
                                    <span
                                        className={`ml-auto shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${
                                            selected ? 'border-white/50 bg-white/20 text-white' : scoreBadgeClass(q.score)
                                        }`}
                                        title="Score out of 10"
                                    >
                                        {formatScore(q.score)}/10
                                    </span>
                                ) : null}
                            </span>
                            <span className="mt-0.5 flex items-center gap-2">
                                {q.skill ? (
                                    <span className={`text-[11px] ${selected ? 'text-sky-50' : 'text-sky-600'}`}>
                                        {q.skill}
                                    </span>
                                ) : null}
                                {formatRange(q) ? (
                                    <span className={`ml-auto shrink-0 text-[10px] tabular-nums ${selected ? 'text-white/80' : 'text-gray-400'}`}>
                                        {formatRange(q)}
                                    </span>
                                ) : null}
                            </span>
                        </button>
                    );
                })}
            </nav>
        </>
    ) : null;

    const mobileQuestionNav = questions.length > 0 ? (
        <div className="md:hidden shrink-0 overflow-x-auto border-b border-gray-200 bg-white">
            <div className="flex gap-2 px-3 py-2 min-w-max">
                <button
                    type="button"
                    onClick={() => selectItem(FULL_KEY)}
                    className={`font-poppins shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${activeKey === FULL_KEY ? 'text-white shadow-sm' : 'bg-gray-100 text-gray-700'}`}
                    style={activeKey === FULL_KEY ? { background: BRAND_GRADIENT } : undefined}
                >
                    Full Interview
                </button>
                {questions.map((q) => {
                    const key = String(q.index);
                    const playingNow = isFullRecording && liveIndex === q.index;
                    return (
                        <button
                            key={key}
                            type="button"
                            disabled={isQuestionDisabled(q)}
                            onClick={() => selectItem(key)}
                            className={`font-poppins shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold disabled:opacity-40 ${activeKey === key ? 'text-white shadow-sm' : playingNow ? 'bg-sky-50 ring-1 ring-sky-300 text-gray-900' : 'bg-gray-100 text-gray-700'}`}
                            style={activeKey === key ? { background: BRAND_GRADIENT } : undefined}
                        >
                            {q.label || `Q${q.index}`}
                        </button>
                    );
                })}
            </div>
        </div>
    ) : null;

    if (status === 'error') {
        return (
            <div className="fixed inset-0 overflow-hidden bg-white flex flex-col">
                {header}
                <div className="flex-1 flex items-center justify-center p-4">
                    <div className="w-full max-w-md bg-white border border-gray-200 rounded-lg p-6 text-center shadow-sm">
                        <h1 className="text-lg font-semibold text-gray-900 mb-2">Unable to load recording</h1>
                        <p className="text-sm text-gray-600 mb-5">{error}</p>
                        <div className="flex gap-2 justify-center">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="font-poppins px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                onClick={() => window.location.reload()}
                                className="font-poppins px-4 py-2 text-sm font-medium rounded-md text-white hover:opacity-90"
                                style={{ background: BRAND_GRADIENT }}
                            >
                                Try again
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (status === 'ready' && videoUrl) {
        return (
            <div className="fixed inset-0 overflow-hidden bg-white flex flex-col">
                {header}
                {mobileQuestionNav}
                <div className="flex-1 flex min-h-0 overflow-hidden">
                    {questions.length > 0 ? (
                        <aside className="hidden md:flex w-72 shrink-0 flex-col min-h-0 border-r border-gray-200 bg-white">
                            {questionNav}
                        </aside>
                    ) : null}
                    <main className="flex-1 min-h-0 overflow-y-auto bg-white">
                        <div className="min-h-full flex items-center justify-center p-6">
                            <div className="w-full max-w-4xl flex flex-col items-center">
                                <h2
                                    className="shrink-0 mb-4 text-center text-xl font-semibold text-gray-900 tracking-tight"
                                    style={{ fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                                >
                                    {playback.title}
                                </h2>
                                <div className="relative w-full rounded-xl overflow-hidden bg-black shadow-xl ring-1 ring-gray-200">
                                    <video
                                        ref={videoRef}
                                        key={playback.src}
                                        src={playback.src}
                                        controls={!showPlayOverlay}
                                        controlsList="nodownload"
                                        playsInline
                                        preload="auto"
                                        onPlay={() => setShowPlayOverlay(false)}
                                        className={`block w-full h-auto bg-black ${showQuestionPanel ? 'max-h-[calc(100vh-22rem)]' : 'max-h-[calc(100vh-13rem)]'}`}
                                    >
                                        Your browser does not support this video format.
                                    </video>
                                    {showPlayOverlay ? (
                                        <button
                                            type="button"
                                            onClick={handlePlayClick}
                                            className="font-poppins absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/35 hover:bg-black/25 transition-colors"
                                            aria-label={`Play ${playback.title}`}
                                        >
                                            <span
                                                className="flex h-16 w-16 items-center justify-center rounded-full text-white shadow-lg shadow-fuchsia-500/30"
                                                style={{ background: BRAND_GRADIENT }}
                                            >
                                                <Play className="h-7 w-7 ml-0.5" fill="currentColor" />
                                            </span>
                                            <span className="text-sm font-semibold text-white drop-shadow" style={{ fontFamily: "'Poppins', sans-serif" }}>
                                                Play recording
                                            </span>
                                        </button>
                                    ) : null}
                                </div>
                                {showQuestionPanel ? (
                                    <div
                                        className={`w-full mt-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 ${
                                            // Fixed height while tracking, so swapping question text
                                            // at a boundary cannot nudge the video up or down.
                                            isFullRecording ? 'h-28 overflow-y-auto' : 'min-h-[6rem]'
                                        }`}
                                    >
                                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                                {panelQuestion?.label || 'Question'}
                                            </span>
                                            {panelQuestion?.skill ? (
                                                <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700">
                                                    {panelQuestion.skill}
                                                </span>
                                            ) : null}
                                            {formatScore(panelQuestion?.score) ? (
                                                <span
                                                    className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${scoreBadgeClass(panelQuestion.score)}`}
                                                    title="Score out of 10"
                                                >
                                                    {formatScore(panelQuestion.score)}/10
                                                </span>
                                            ) : null}
                                            {formatRange(panelQuestion) ? (
                                                <span className="ml-auto text-[11px] tabular-nums text-gray-500">
                                                    {formatRange(panelQuestion)}
                                                </span>
                                            ) : null}
                                        </div>
                                        <p className="text-sm leading-relaxed text-gray-800 whitespace-pre-line break-words">
                                            {panelQuestion?.question_text || (
                                                <span className="text-gray-400">Waiting for the first question…</span>
                                            )}
                                        </p>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 overflow-hidden bg-white flex flex-col">
            {header}
            <div className="flex-1 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="mx-auto mb-3 h-8 w-8 rounded-full border-2 border-gray-200 border-t-sky-500 animate-spin" />
                    <p className="text-sm text-gray-600">Loading recording…</p>
                </div>
            </div>
        </div>
    );
}
